<template>
  <client-only>
    <div>
      <div
        v-if="!isDesktop"
        class="toast-modal lg:hidden"
        :class="{ 'toast-modal--open': isOpen }"
      >
        <div class="toast-modal-backdrop" />
        <div class="toast-modal-content">
          <div
            class="absolute top-0 right-0 left-0 bottom-0 focus:outline-none"
            @click="onModalClose"
          />
          <div class="toast-modal-content-box">
            <ToastModalheader
              v-if="title"
              :title="title"
              :show-close-button="showCloseButton && !preventClose"
              @close="onModalClose"
            />
            <div
              ref="modalBody"
              class="px-4 overflow-y-scroll toast-modal-content-box-body"
              :class="{ 'pb-10': !showMobileFooter }"
              tabindex="0"
              role="region"
              :aria-label="`Modal content for ${modalId}`"
            >
              <slot />
            </div>
            <slot v-if="showMobileFooter" name="footer" />
          </div>
        </div>
      </div>
      <Modal
        v-else-if="isDesktop"
        :width="width"
        height="auto"
        adaptive
        :name="modalId"
        :click-to-close="!preventClose"
        class="hidden lg:block"
        @closed="onModalClose"
      >
        <ToastModalheader
          v-if="title"
          class="lg:mx-9"
          :title="title"
          :show-close-button="showCloseButton && !preventClose"
          @close="onModalClose"
        />
        <div
          class="modal-body disable-scrollbars"
          :class="{ 'pb-0': hasFooterSlot }"
        >
          <slot />
        </div>
        <slot name="footer" />
      </Modal>
    </div>
  </client-only>
</template>

<script>
import { togglePageScroll } from '@/utils/general'
import ToastModalheader from '@/components/ToastModal/ToastModalheader'
import { ScreenBreakpoints } from '@/enums/general'
import { vueWindowSizeMixin } from 'vue-window-size'

export default {
  name: 'ToastModal',
  components: {
    ToastModalheader
  },
  mixins: [vueWindowSizeMixin],
  props: {
    title: {
      type: String,
      default: ''
    },
    width: {
      type: Number,
      default: 407
    },
    showMobileFooter: {
      type: Boolean,
      default: false
    },
    modalId: {
      type: String,
      required: true
    },
    preventClose: {
      type: Boolean,
      default: false
    },
    showCloseButton: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isOpen: false
    }
  },
  computed: {
    isDesktop () {
      return this.windowWidth > ScreenBreakpoints.lg
    },
    hasFooterSlot () {
      return !!this.$slots.footer
    }
  },
  watch: {
    isDesktop (newIsDesktop, oldIsDesktop) {
      if (newIsDesktop && !oldIsDesktop && this.isOpen) {
        setTimeout(() => {
          this.toggleActivation(true)
        }, 100)
      }
    }
  },
  methods: {
    toggleActivation (isOpen = true) {
      this.$modal[isOpen ? 'show' : 'hide'](this.modalId)
      togglePageScroll(isOpen)
      this.isOpen = isOpen

      if (isOpen) {
        const scrollableElement = this.$refs?.modalBody

        if (scrollableElement) {
          scrollableElement.scrollTop = 0
        }
      }
    },
    onModalClose () {
      if (this.preventClose) {
        return
      }

      togglePageScroll(false)
      this.$modal.hide(this.modalId)
      this.isOpen = false
      this.$emit('closed')
    }
  }
}
</script>

<style lang="postcss" scoped>
.toast-modal {
  @apply fixed left-0 h-full w-full;
  z-index: 100;
  top: 100%;
  --animation-duration: .6s;
  transition: top 0s var(--animation-duration);

  &--open {
    @apply top-0;
    transition: top 0s 0s;

    .toast-modal-content {
      transform: translate3d(0, -100%, 0);
    }

    .toast-modal-backdrop {
      @apply opacity-25 transition-opacity ease-in;
      transition-duration: var(--animation-duration);
    }
  }

  &-backdrop {
    @apply absolute left-0 right-0 bottom-0 top-0 bg-black opacity-0 transition-opacity ease-in;
    transition-duration: var(--animation-duration);
  }

  &-content {
    @apply absolute top-0 max-w-full h-full w-full transition-transform duration-500 flex items-end;
    top: 100%;
    transition-duration: var(--animation-duration);
    transform: translate3d(0, 0, 0);

    &-box {
      @apply w-full relative z-70 bg-neutral-00 flex flex-col;
      box-shadow: 0px 0px 0px 0px rgba(0, 0, 0, 0.10), 0px 6px 13px 0px rgba(0, 0, 0, 0.10), 0px 23px 23px 0px rgba(0, 0, 0, 0.09), 0px 52px 31px 0px rgba(0, 0, 0, 0.05), 0px 92px 37px 0px rgba(0, 0, 0, 0.01), 0px 143px 40px 0px rgba(0, 0, 0, 0.00);
      border-radius: 20px 20px 0px 0px;
      max-height: 80vh;
    }
  }

}

>>> .vm--modal {
  @apply flex flex-col;
  border-radius: 2rem;

  .modal-body {
    @apply px-9 pb-6 overflow-y-scroll;

    &.pb-0 {
      @apply pb-0 !important;
    }
  }
}

@screen lg {
  >>> .vm--modal {
    max-height: 90%;
  }
}
</style>
